import { apiFetch } from './http';

/** One step of a guided test, as the client shows it: what to do and what should be seen. */
export interface TestStep {
  id: string;
  text: string;
  expect?: string;
  /** The ledger row this step's answer is evidence for (docs/verification/LEDGER.md). */
  ledgerRow?: string;
  /** What the step is about, for its icon (app/testPhases.ts); read from its words when left out. */
  phase?: string;
  /** The answer that says the step holds; "yes" when left out. Only an answer that differs takes a report. */
  goodAnswer?: 'yes' | 'no';
  /** On a web card, where the step starts: a path of this web app, never another site's address. */
  url?: string;
  /** Who the step is written for, as a hint beside it: a role's name. */
  role?: string;
}

/** Where a card's steps are done: in the game, or in the browser. */
export type TestMedium = 'game' | 'web';
/** Who a card is offered to: every tester, or the administrators alone. */
export type TestAudience = 'testers' | 'admins';

/**
 * Where a web card's answer was given from, kept beside it: what the page
 * said of itself (`client`) and what the platform knew (`server`). The
 * answerer's own and the reviewer's; absent for anybody else.
 */
export interface AnswerContext {
  client?: { path?: string; width?: number; height?: number; userAgent?: string };
  server?: { platformVersion?: string; roles?: string[]; at?: string };
}

export interface TestQuestion {
  text: string;
  options: string[];
  pictureUrl?: string;
}

export interface TestEntry {
  id: number;
  kind: 'text' | 'file' | 'answer';
  body: string | null;
  fileName: string | null;
  by: string | null;
  at: string;
  url: string | null;
}

/** The three answers a step takes, the same the game's keys send. */
export type StepAnswer = 'yes' | 'no' | 'cant';

/**
 * A step's answer: the session's own events carry the game's (a claim from the
 * client, shown as one), and the tester's correction on the page is read over
 * it. `answer` is the effective one, null when only a comment was written on a
 * step the game never answered; `originalAnswer` is the game's.
 */
export interface TestAnswer {
  stepId: string;
  answer: StepAnswer | string | null;
  reportId: string;
  player: string;
  at: number;
  note?: string | null;
  originalAnswer?: string | null;
  edited?: boolean;
  /** Who said it: the account's handle when the platform knows the player, the name in game otherwise. */
  by?: string;
  /** The reader's own answer: the one their page edits. */
  mine?: boolean;
  /** A web card's state capture; null or absent on a game card's answers. */
  context?: AnswerContext | null;
}

/** A person's comment on the test as a whole, saved as they write it. */
export interface TestNote {
  by: string;
  note: string;
  at: string;
  mine: boolean;
}

/** A report sent from the game with F9 (`why` is `report`), or the dump a "no" took (`step`). */
export interface TestReport {
  id: string;
  why: string;
  player: string;
  by: string;
  /** Seconds into the mission. */
  at: number;
  note: string;
  grid: string;
  url: string | null;
  thumbUrl: string | null;
  mine: boolean;
}

export type TestKind = 'play' | 'lend' | 'judge';
/** Who plays a card: a person, or the agent driving a lent machine. Declared when the card is made. */
export type TestPerformer = 'person' | 'agent';

/**
 * Where a claimed lend task's machine is: the connect job the task queued for
 * the tester's watcher (null until the session's mission is up), what the
 * watcher answered, whether the platform has let the machine go, and whether
 * the watcher is there and may be lent right now.
 */
export interface LendState {
  jobId: number | null;
  job: 'pending' | 'taken' | 'done' | 'failed' | 'cancelled' | null;
  detail: string | null;
  released: boolean;
  present: boolean;
  lendable: boolean;
}
export type TestState = 'open' | 'claimed' | 'done' | 'closed' | 'cancelled';

/**
 * Where a play card's session is: made and not started, started and loading
 * (nobody can join yet), up (the mission has reported in), or over.
 */
export type CardServerState = 'waiting' | 'starting' | 'ready' | 'ended';

/** A play card's session, as its page reads it. */
export interface CardServerView {
  state: CardServerState;
  since: string | null;
  outcome: string | null;
  /**
   * While the session is live: whether a player's game was heard in the last
   * minute and a half (the test instruments report every thirty seconds).
   * Absent on an older platform.
   */
  inGame?: boolean;
  /** When a player's game was last heard; null when never. */
  playerHeardAt?: string | null;
  /** Whether the reader may stop the server by hand: whoever took the card, its author, an admin. */
  canStop?: boolean;
}

export interface TestTask {
  id: number;
  kind: TestKind;
  track: string | null;
  title: string;
  brief: string | null;
  sessionCode: string | null;
  durationMinutes: number;
  needed: number;
  steps: TestStep[] | null;
  question: TestQuestion | null;
  state: TestState;
  createdAt: string;
  claimedBy: string | null;
  claimedAt: string | null;
  result: { option?: string; answers?: TestAnswer[] } | null;
  doneAt: string | null;
  verdict: string | null;
  closedAt: string | null;
  closedNote: string | null;
  announced: boolean;
  mine: boolean;
  entries: TestEntry[];
  answers: TestAnswer[];
  /** Whether the reader writes on this test: whoever took it, an admin, and everybody who played its session. */
  canWrite?: boolean;
  /** Everybody's comment on the test as a whole. Absent on an older platform. */
  notes?: TestNote[];
  /** What the players reported from the game. Absent on an older platform. */
  reports?: TestReport[];
  /** Absent on an answer from before the field: a person, as every card was. */
  performer?: TestPerformer;
  /** Only on a claimed lend task. */
  lend?: LendState | null;
  /** A "test card": the one mission this task is about, and the server it starts on. */
  missionCode: string | null;
  panelServerId: number | null;
  /** Where the card's session is, and since when. Null with no session; absent on an older platform. */
  server?: CardServerView | null;
  /**
   * What the platform's reading of each step's wording warns about, by step id
   * (only the steps with a warning). An admin's; null or absent for anybody else.
   */
  stepWarnings?: Record<string, StepWarning[]> | null;
  /** Absent on an older platform: a game card, as every card was. */
  medium?: TestMedium;
  /** The platform's version when the card was made; null on a card from before the field. */
  platformVersion?: string | null;
  /** The roadmap item the card verifies. */
  roadmapItem?: string | null;
  audience?: TestAudience;
  /** Made and not announced: an admin's until an admin opens it. */
  draft?: boolean;
  /** Made on a release older than the running one: still answerable, and said. */
  olderVersion?: boolean;
}

/** A way a step's wording could make a right observation a wrong answer (the platform's StepWording). */
export type StepWarning =
  | 'no_question'
  | 'several_questions'
  | 'conditional'
  | 'joined_facts'
  | 'negative_opening'
  | 'too_long'
  | 'not_in_mission';

export interface TestList {
  open: TestTask[];
  history: TestTask[];
  uploads: boolean;
}

export interface TestEvidence {
  yes: number;
  no: number;
  cant: number;
  tasks: number[];
  sessions: string[];
}

export interface CreateTest {
  kind: TestKind;
  track?: string;
  title: string;
  brief?: string;
  sessionCode?: string;
  durationMinutes?: number;
  needed?: number;
  steps?: TestStep[];
  question?: TestQuestion;
  performer?: TestPerformer;
  medium?: TestMedium;
  audience?: TestAudience;
  roadmapItem?: string;
  draft?: boolean;
}

/** One card of a batch file (docs/verification/web-cards-*.json): what `importTests` takes. */
export interface ImportCard {
  id?: string;
  title: string;
  description?: string | null;
  medium?: TestMedium;
  audience?: TestAudience;
  roadmapItem?: string | null;
  durationMinutes?: number;
  steps: (Omit<TestStep, 'expect' | 'ledgerRow' | 'goodAnswer' | 'url'> & {
    expect?: string | null;
    ledgerRow?: string | null;
    goodAnswer?: 'yes' | 'no' | null;
    url?: string | null;
  })[];
}

export interface ImportResult {
  created: TestTask[];
  /** Titles left out: a card by that title, of that medium, is already there and not over. */
  skipped: string[];
}

export const fetchTests = () => apiFetch<TestList>('/api/tests');

/** `todo`: a card to take or being played; `review`: waits for a verdict; `done`: has one; `skip`: needs no review. */
export type InboxTray = 'todo' | 'review' | 'done' | 'skip';
export type InboxType = 'play' | 'lend' | 'judge' | 'campaign' | 'captures' | 'smoke';

/** One row of the tests page: a card, an inspection campaign, a session's loose captures or a smoke night. */
export interface InboxRow {
  type: InboxType;
  key: string;
  /** The card's own title, a campaign's recipe and terrain when it ran one, a session's code; null when the page names it. */
  title: string | null;
  tray: InboxTray;
  state: string;
  /** Why the row needs no review; null in any other tray. */
  reason: string | null;
  link: string;
  date: string | null;
  version: string | null;
  total: number | null;
  reviewed: number | null;
  problems: number | null;
  performer: TestPerformer | null;
  who: string | null;
  sessionCode: string | null;
  tours: number | null;
  toursToReview: number | null;
  supersededBy: number | null;
  verdict: string | null;
  note: string | null;
  previews: string[];
  /** A card's own: where its steps are done. Null on every other row; absent on an older platform. */
  medium?: TestMedium | null;
}

export interface Inbox {
  rows: InboxRow[];
  counts: Record<InboxTray, number>;
  admin: boolean;
  /** A tester's: their own cards, and the two finished trays whole. Absent on an older platform. */
  tester?: boolean;
}

/** Every test and every review in one list; somebody who is not an admin gets the cards they took. */
export const fetchInbox = () => apiFetch<Inbox>('/api/tests/inbox');

/** An admin says who played these cards. */
export const setTestPerformer = (ids: number[], performer: TestPerformer) =>
  apiFetch<TestTask[]>('/api/tests/performer', { method: 'PUT', body: { ids, performer } });
export const fetchTest = (id: number) => apiFetch<TestTask>(`/api/tests/${id}`);
export const fetchTestEvidence = () =>
  apiFetch<Record<string, TestEvidence>>('/api/tests/evidence');
export const createTest = (body: CreateTest) =>
  apiFetch<TestTask>('/api/tests', { method: 'POST', body });
export const claimTest = (id: number) =>
  apiFetch<TestTask>(`/api/tests/${id}/claim`, { method: 'POST' });
export const finishTest = (id: number, body: { option?: string; note?: string }) =>
  apiFetch<TestTask>(`/api/tests/${id}/done`, { method: 'POST', body });
export const noteTest = (id: number, text: string) =>
  apiFetch<TestTask>(`/api/tests/${id}/notes`, { method: 'POST', body: { text } });
/** The tester's correction of one step: the answer he meant, a comment, or both; a field left out is kept. */
export const editTestStep = (
  id: number,
  stepId: string,
  body: { answer?: StepAnswer; note?: string; context?: PageState },
) =>
  apiFetch<TestTask>(`/api/tests/${id}/steps/${encodeURIComponent(stepId)}`, {
    method: 'PUT',
    body,
  });
/** An admin adds a step to a play card nobody has taken: at the end, or after the step `after` names. */
export const addTestStep = (id: number, step: TestStep, after?: string) =>
  apiFetch<TestTask>(
    `/api/tests/${id}/steps${after ? `?after=${encodeURIComponent(after)}` : ''}`,
    { method: 'POST', body: step },
  );
/** An admin removes a step from a play card nobody has taken (409 `last_step` for the only one). */
export const removeTestStep = (id: number, stepId: string) =>
  apiFetch<TestTask>(`/api/tests/${id}/steps/${encodeURIComponent(stepId)}`, {
    method: 'DELETE',
  });
/** What a page says of itself when a web card's step is answered on it: the platform keeps it beside the answer. */
export interface PageState {
  path: string;
  width: number;
  height: number;
}

/** The page the reader is on, now. No picture and nothing of what the page shows. */
export const pageState = (): PageState => ({
  path: window.location.pathname + window.location.search,
  width: window.innerWidth,
  height: window.innerHeight,
});

/** The web cards the reader took and has not finished, the last taken first. */
export const fetchMyWebCards = () => apiFetch<TestTask[]>('/api/tests/mine/web');
/** An admin's: a batch of cards, every one made as a draft. */
export const importTests = (cards: ImportCard[]) =>
  apiFetch<ImportResult>('/api/tests/import', { method: 'POST', body: cards });
/** An admin opens one draft: it is offered and announced from now on. */
export const openTestDraft = (id: number) =>
  apiFetch<TestTask>(`/api/tests/${id}/open`, { method: 'POST' });
/** An admin opens these drafts, or every draft there is when none is named. */
export const openTestDrafts = (ids?: number[]) =>
  apiFetch<TestTask[]>('/api/tests/open', { method: 'POST', body: ids ? { ids } : {} });

/** The step id of a person's comment on the whole test. */
export const CARD_STEP = '_card';

/**
 * "Somebody wrote on this test": calls back every time, so the page reads the
 * test again. Nothing where the browser has no EventSource; the page's own
 * poll is what is left then.
 */
export function subscribeTest(id: number, onChanged: () => void): (() => void) | null {
  if (typeof EventSource === 'undefined') return null;
  const source = new EventSource(`/api/tests/${id}/stream`);
  source.addEventListener('test', onChanged);
  return () => source.close();
}

export const closeTest = (id: number, verdict: string, note: string) =>
  apiFetch<TestTask>(`/api/tests/${id}/close`, { method: 'POST', body: { verdict, note } });
/** A cancelled card, deleted for good (an admin's; 409 `not_cancelled` for any other). */
export const deleteTest = (id: number) =>
  apiFetch<null>(`/api/tests/${id}?purge=true`, { method: 'DELETE' });
export const cancelTest = (id: number) =>
  apiFetch<TestTask>(`/api/tests/${id}`, { method: 'DELETE' });
/** Starts the card's server: idempotent while its session has not ended, otherwise creates and starts one. */
export const startTestServer = (id: number, endOther = false) =>
  apiFetch<TestTask>(`/api/tests/${id}/server${endOther ? '?endOther=true' : ''}`, {
    method: 'POST',
  });
/**
 * «Atura el servidor»: ends the card's own session and stops its server,
 * whatever the card's state. 403 `not_yours_to_stop`; 409 `no_own_session`,
 * `server_not_up` or `shared_session`.
 */
export const stopTestServer = (id: number) =>
  apiFetch<TestTask>(`/api/tests/${id}/stop-server`, { method: 'POST' });
/**
 * What a 409 `server_busy` says: the session that holds the card's server, the
 * test card it belongs to when it is a card's, and whether the caller may end
 * it from here (`startTestServer(id, true)`).
 */
export type ServerBusy = {
  sessionCode: string;
  cardId?: number;
  cardTitle?: string;
  cardState?: TestState;
  canEnd?: boolean;
};
/** An admin sets or changes which mission and server a test card starts. */
export const setTestMission = (id: number, body: { missionCode: string; panelServerId?: number }) =>
  apiFetch<TestTask>(`/api/tests/${id}/mission`, { method: 'PUT', body });

/** A file dropped on the page: multipart, so not through apiFetch's JSON body. */
export async function uploadTestFile(id: number, file: File): Promise<TestTask> {
  const form = new FormData();
  form.append('file', file);
  const token = document.cookie
    .split('; ')
    .find((row) => row.startsWith('XSRF-TOKEN='))
    ?.slice('XSRF-TOKEN='.length);
  const response = await fetch(`/api/tests/${id}/files`, {
    method: 'POST',
    headers: token ? { 'X-XSRF-TOKEN': decodeURIComponent(token) } : {},
    credentials: 'same-origin',
    body: form,
  });
  if (!response.ok) throw new Error(`upload_${response.status}`);
  return (await response.json()) as TestTask;
}

/** Everything the platform has about a session, merged in time order (GET /api/sessions/{code}/timeline). */
export interface TimelineItem {
  at: string;
  t: number | null;
  source: 'runtime' | 'log' | 'watcher' | 'task';
  kind: string;
  summary: string;
  payload: unknown;
}

export interface TimelineView {
  code: string;
  state: string;
  startedAt: string | null;
  endedAt: string | null;
  sources: Record<string, string>;
  items: TimelineItem[];
}

export const fetchTimeline = (code: string) =>
  apiFetch<TimelineView>(`/api/sessions/${code}/timeline`);
